import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Alert
} from '@mui/material';
import { ArrowBack, ZoomIn, ZoomOut, Refresh } from '@mui/icons-material';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface RefundPolicyProps {
  appName: string;
  pdfUrl: string;
  title: string;
}

const RefundPolicy: React.FC<RefundPolicyProps> = ({ appName, pdfUrl, title }) => {
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [scale, setScale] = useState<number>(1.0);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError('');
  };

  const onDocumentLoadError = (error: Error) => {
    setLoading(false);
    setError(`Failed to load PDF: ${error.message}`);
  };

  const retryLoad = () => {
    setLoading(true);
    setError('');
  };

  const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 2.0));
  };

  const handleZoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  };

  const goToPrevPage = () => {
    setPageNumber(prevPageNumber => Math.max(prevPageNumber - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prevPageNumber => Math.min(prevPageNumber + 1, numPages));
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#022179' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton color="inherit" onClick={retryLoad} title="Retry Loading">
              <Refresh />
            </IconButton>
            <IconButton color="inherit" onClick={handleZoomOut} disabled={loading}>
              <ZoomOut />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton color="inherit" onClick={handleZoomIn} disabled={loading}>
              <ZoomIn />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={retryLoad}>
                Retry
              </Button>
            }
          >
            Failed to load refund and cancellation policy document. Please try refreshing the page.
          </Alert>
        )}

        {/* Page Navigation */}
        {!loading && numPages > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
            <Button
              variant="outlined"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              sx={{ mr: 2 }}
            >
              Previous
            </Button>
            <Typography variant="body1" sx={{ mx: 2 }}>
              Page {pageNumber} of {numPages}
            </Typography>
            <Button
              variant="outlined"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              sx={{ ml: 2 }}
            >
              Next
            </Button>
          </Box>
        )}

        {/* PDF Viewer */}
        <Paper 
          elevation={3} 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '600px',
            p: 2,
            backgroundColor: 'white'
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Loading Refund and Cancellation Policy...</Typography>
            </Box>
          )}
          
          {!error && (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading=""
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          )}
        </Paper>

        {/* Footer Navigation */}
        {!loading && numPages > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              sx={{ mr: 2 }}
            >
              Previous
            </Button>
            <Typography variant="body1" sx={{ mx: 2 }}>
              Page {pageNumber} of {numPages}
            </Typography>
            <Button
              variant="outlined"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              sx={{ ml: 2 }}
            >
              Next
            </Button>
          </Box>
        )}

        {/* Disclaimer */}
        <Box sx={{ mt: 4, p: 3, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: '#1565c0', textAlign: 'center' }}>
            This refund and cancellation policy is effective and governs your use of {appName}.
            For any questions regarding this policy, please contact us at contact@prodxpress.com
            {(appName.toLowerCase().includes('tracsess')) && (
              <>
                {' | '}
                <a 
                  href="https://tracsess.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1565c0', fontWeight: 600 }}
                >
                  Visit tracsess.com
                </a>
              </>
            )}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default RefundPolicy; 